import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Spinner, Table } from 'reactstrap';

import PatientManagementService from 'services/patientsManagement';
import { REVIEW_DECISION, VERIFICATION_STATUS } from 'constants/documentVerification';
import {
    dobsMatch,
    formatScorePercent,
    getMatchBand,
    namesMatch,
    normalizeDob,
} from 'helpers/idVerification';

const POLL_INTERVAL_MS = 2500;
// ~3 minutes. The first check on a cold service is slow because the OCR model
// downloads its weights; 60s was too short, so polling gave up while the job was
// still running and staff had to refresh to see the result. If the ceiling is
// still hit, we surface a "taking longer" state (below) instead of a dead spinner.
const MAX_POLL_ATTEMPTS = 72;

const FAILURE_MESSAGES = {
    no_face_detected: 'No face could be detected in the selfie or ID — needs manual review.',
    image_quality_low: 'Image quality is too low to run the check — needs manual review.',
    invalid_request: 'The document is missing an image or selfie — needs manual review.',
};

const RUNNING_STATUSES = [VERIFICATION_STATUS.PENDING, VERIFICATION_STATUS.PROCESSING];

const isRunningStatus = (status) => RUNNING_STATUSES.includes(status);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function MatchIndicator({ matches }) {
    if (matches === null) {
        return <span className="text-muted">Not detected</span>;
    }
    return matches
        ? <span className="text-success">✓ match</span>
        : <span className="text-danger font-weight-bold">✕ mismatch</span>;
}

export default function IdVerificationPanel({ documentId, patientInfo, hasImages, onReviewed }) {
    const [verification, setVerification] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRunning, setIsRunning] = useState(false);
    const [isReviewing, setIsReviewing] = useState(false);
    // The poll loop hit its ceiling while the job was still running — the result
    // isn't in yet, but the check is still working server-side. Distinct from the
    // active-running state so staff aren't shown a spinner that never resolves.
    const [pollExhausted, setPollExhausted] = useState(false);
    const isMountedRef = useRef(true);
    // Guard so the first-visit auto-check fires at most once per document, even
    // under React StrictMode's double-invoke — a check costs a Rekognition call.
    const autoStartedForRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const pollUntilTerminal = useCallback(async (initial) => {
        let current = initial;
        let attempt = 0;
        while (isMountedRef.current && attempt < MAX_POLL_ATTEMPTS && isRunningStatus(current?.status)) {
            await delay(POLL_INTERVAL_MS);
            if (!isMountedRef.current) {
                break;
            }
            const res = await PatientManagementService.getDocumentVerificationStatus(documentId);
            current = res?.data;
            setVerification(current);
            attempt += 1;
        }
        return current;
    }, [documentId]);

    const runCheck = useCallback(async () => {
        setPollExhausted(false);
        setIsRunning(true);
        try {
            const res = await PatientManagementService.startDocumentVerification(documentId);
            setVerification(res?.data);
            const settled = await pollUntilTerminal(res?.data);
            if (isMountedRef.current) {
                setVerification(settled);
                // Poll gave up before the job finished — keep it visible, don't fake a spinner.
                setPollExhausted(isRunningStatus(settled?.status));
            }
        } catch (error) {
            // interceptor toasts the error
        } finally {
            if (isMountedRef.current) {
                setIsRunning(false);
            }
        }
    }, [documentId, pollUntilTerminal]);

    // Re-check the status of an already-running job WITHOUT starting (and re-charging)
    // a new check. Used by the "Check for result" button after the poll ceiling is hit.
    const refreshStatus = useCallback(async () => {
        setPollExhausted(false);
        try {
            const res = await PatientManagementService.getDocumentVerificationStatus(documentId);
            const current = res?.data;
            if (!isMountedRef.current) {
                return;
            }
            setVerification(current);
            if (isRunningStatus(current?.status)) {
                const settled = await pollUntilTerminal(current);
                if (isMountedRef.current) {
                    setVerification(settled);
                    setPollExhausted(isRunningStatus(settled?.status));
                }
            }
        } catch (error) {
            // interceptor toasts the error
        }
    }, [documentId, pollUntilTerminal]);

    // On mount: show a previously-run check so staff don't re-run (and re-charge) it,
    // and — if this patient has never been checked — auto-start the first check once,
    // provided both an ID and a selfie are on file.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await PatientManagementService.getDocumentVerificationStatus(documentId);
                const current = res?.data;
                if (cancelled || !isMountedRef.current) {
                    return;
                }

                const neverRun = !current || current.status === VERIFICATION_STATUS.NONE;
                if (neverRun) {
                    // First-ever visit: kick the check off automatically, once, only when
                    // there's actually something to check. Any prior run (pending/completed/
                    // failed) is a non-NONE status and skips this — no auto re-trigger.
                    if (hasImages && autoStartedForRef.current !== documentId) {
                        autoStartedForRef.current = documentId;
                        setIsLoading(false);
                        await runCheck();
                    }
                    return;
                }

                setVerification(current);
                if (isRunningStatus(current.status)) {
                    const settled = await pollUntilTerminal(current);
                    if (!cancelled && isMountedRef.current) {
                        setVerification(settled);
                        setPollExhausted(isRunningStatus(settled?.status));
                    }
                }
            } catch (error) {
                // The axios interceptor surfaces the error toast; nothing to add here.
            } finally {
                if (!cancelled && isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [documentId, hasImages, pollUntilTerminal, runCheck]);

    const submitReview = useCallback(async (reviewDecision) => {
        setIsReviewing(true);
        try {
            const res = await PatientManagementService.reviewDocumentVerification(documentId, { reviewDecision });
            setVerification(res?.data);
            if (onReviewed) {
                await onReviewed(reviewDecision);
            }
        } catch (error) {
            // interceptor toasts the error
        } finally {
            if (isMountedRef.current) {
                setIsReviewing(false);
            }
        }
    }, [documentId, onReviewed]);

    const status = verification?.status;
    const isProcessing = isRunning || isRunningStatus(status);
    const ocr = verification?.ocr || {};

    const enteredName = [patientInfo?.firstName, patientInfo?.lastName].filter(Boolean).join(' ') || '—';
    const enteredDob = normalizeDob(patientInfo?.birthday) || '—';
    const band = getMatchBand(verification?.matchScore);

    return (
        <section className="id-verification-panel border-top pt-2 mt-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="status-label mb-0">ID verification</label>
                {verification?.reviewDecision && (
                    <Badge color="dark" pill className="text-capitalize font-size-12">
                        Decision: {verification.reviewDecision}
                    </Badge>
                )}
            </div>

            {isLoading && (
                <div className="d-flex align-items-center">
                    <Spinner size="sm" color="info" className="mr-2" />
                    <span>Loading ID check…</span>
                </div>
            )}

            {/* Nothing to check yet — don't offer (or auto-fire) a check with no images. */}
            {!isLoading && !verification && !isProcessing && !pollExhausted && !hasImages && (
                <small className="text-muted d-block">
                    Add both an ID photo and a selfie to run an ID check.
                </small>
            )}

            {/* Manual fallback: only reached if the auto-check didn't fire (e.g. images
                added after load). First visits with images auto-start instead. */}
            {!isLoading && !verification && !isProcessing && !pollExhausted && hasImages && (
                <button className="btn btn-outline-primary status-button" onClick={runCheck}>
                    Run ID check
                </button>
            )}

            {!isLoading && isProcessing && !pollExhausted && (
                <div className="d-flex align-items-center">
                    <Spinner size="sm" color="info" className="mr-2" />
                    <span>Running ID check… <small className="text-muted">the first check can take a minute or two while the model warms up.</small></span>
                </div>
            )}

            {!isLoading && pollExhausted && (
                <div className="alert alert-info py-2 mb-2" role="alert">
                    <div className="mb-2">
                        This is taking longer than usual — the check is still running in the background
                        (the first check warms up the model). It hasn’t failed.
                    </div>
                    <button className="btn btn-outline-primary status-button" onClick={refreshStatus}>
                        Check for result
                    </button>
                </div>
            )}

            {!isLoading && !isProcessing && !pollExhausted && status === VERIFICATION_STATUS.FAILED && (
                <div className="alert alert-warning py-2 mb-2" role="alert">
                    {FAILURE_MESSAGES[verification.reasonCode] || verification.reason
                        || 'The check could not be completed — needs manual review.'}
                </div>
            )}

            {!isLoading && !isProcessing && !pollExhausted && status === VERIFICATION_STATUS.COMPLETED && (
                <>
                    <div className="mb-2">
                        <span className="mr-2">Face match:</span>
                        <Badge color={band.color} pill className="font-size-12">{band.label}</Badge>
                        <small className="text-muted ml-2">({formatScorePercent(verification.matchScore)})</small>
                    </div>

                    <Table borderless size="sm" className="mb-2 id-verification-table">
                        <thead>
                            <tr className="text-muted">
                                <th className="pl-0"> </th>
                                <th>Patient entered</th>
                                <th>Detected on ID</th>
                                <th> </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="pl-0 text-muted">Name</td>
                                <td>{enteredName}</td>
                                <td>{ocr.name || <span className="text-muted">—</span>}</td>
                                <td><MatchIndicator matches={namesMatch(ocr.name, enteredName)} /></td>
                            </tr>
                            <tr>
                                <td className="pl-0 text-muted">DOB</td>
                                <td>{enteredDob}</td>
                                <td>{ocr.dob || <span className="text-muted">—</span>}</td>
                                <td><MatchIndicator matches={dobsMatch(ocr.dob, patientInfo?.birthday)} /></td>
                            </tr>
                        </tbody>
                    </Table>

                    {ocr.reasonCode && (
                        <small className="text-muted d-block mb-2">
                            OCR could not read the ID confidently — verify the fields manually.
                        </small>
                    )}
                </>
            )}

            {!isLoading && !isProcessing && !pollExhausted && verification && (
                <section className="d-flex flex-row align-items-center mt-1">
                    <button
                        className="btn btn-primary status-button mr-2"
                        disabled={isReviewing}
                        onClick={() => submitReview(REVIEW_DECISION.APPROVED)}
                    >
                        Approve
                    </button>
                    <button
                        className="btn btn-outline-danger status-button mr-2"
                        disabled={isReviewing}
                        onClick={() => submitReview(REVIEW_DECISION.REJECTED)}
                    >
                        Reject
                    </button>
                    <button
                        className="btn btn-outline-warning status-button mr-3"
                        disabled={isReviewing}
                        onClick={() => submitReview(REVIEW_DECISION.ESCALATED)}
                    >
                        Escalate
                    </button>
                    <button
                        className="btn btn-link p-0 compare-label"
                        disabled={isReviewing}
                        onClick={runCheck}
                    >
                        Re-run check
                    </button>
                </section>
            )}
        </section>
    );
}
